/**
 * Guest delivery's review, as the Suite reads it (pure, no React): Ivin's
 * review screen (DeliveryReview.jsx) and the Captures tab both use it.
 *
 * The server mirrors each processed capture onto its capture attempt as
 * `delivery` (v: 2, functions/src/services/delivery.ts mirrorOf), with a
 * `review` block: the review state, what guests get (kind), the run guests
 * see (active, approved) and the newest run waiting for Ivin (candidate),
 * each with the processing machine's checks and facts. Nothing reaches a
 * guest before Ivin approves it: the model (the approval is bound to that
 * exact run) or the photographs alone. Contract:
 * docs/ingest/15-guest-delivery-v2-contract.md section 5.1.
 *
 * The guardian check (design 6.7): a registration marked under 18 is emailed
 * nothing, delivery or download code, until staff confirm that the address on
 * it belongs to the parent or guardian (personFields.guardianRecipient, for
 * that exact address; any change of address clears it).
 */

// Only the v2 record (one row per member); anything older is not shown.
export const deliveryOf = (a) =>
  (a?.delivery && typeof a.delivery === "object" && a.delivery.v === 2 && Array.isArray(a.delivery.members) ? a.delivery : null);

export const snOf = (a) => a?.id ?? a?.sn ?? "";

// A capture's place in the review:
//   review         a model waits for Ivin
//   held           he held the model waiting
//   reprocess      he asked for the capture to be processed again
//   approved       guests get its approved model, and nothing waits
//   photos_only    guests get its photographs alone
//   not_processed  no model reported yet
//   left_out       never delivered (Leave out)
//   deleted        its files are gone (everyone in it deleted, or past their dates)
export function stageOf(a) {
  const d = deliveryOf(a);
  if (!d) return "not_processed";
  const r = d.review && typeof d.review === "object" ? d.review : null;
  if (r?.state === "left_out") return "left_out";
  if (d.status === "deleted") return "deleted";
  if (!r) return d.status === "review" ? "review" : "approved";
  if (r.candidate) {
    if (r.state === "held") return "held";
    if (r.state === "reprocess") return "reprocess";
    return "review";
  }
  if (r.state === "reprocess") return "reprocess";
  if (r.kind === "photos_only") return "photos_only";
  if (r.kind === "full" && r.active) return "approved";
  return "not_processed";
}

// The run the reviewer is shown: the candidate, else the run guests see.
export const shownRunOf = (a) => {
  const r = deliveryOf(a)?.review;
  return r?.candidate ?? r?.active ?? null;
};
export const checksOf = (run) => (Array.isArray(run?.checks) ? run.checks.filter((c) => c && typeof c.check === "string") : []);
export const failedChecksOf = (run) => checksOf(run).filter((c) => c.ok !== true);
// Passed every check: at least one check reported, and none failed. A run
// without checks never counts as passed (Ivin looks at it himself).
export const passedChecks = (run) => checksOf(run).length > 0 && failedChecksOf(run).length === 0;

/* ── the guardian check ── */

export const isMinorEntry = (entry) => {
  const pf = entry?.personFields ?? {};
  return pf.isMinor === true || pf.guardianConsent === true || pf.consent?.byGuardian === true;
};
const norm = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");
// "adult": no check needed; "confirmed": staff confirmed the address on the
// entry now; "needed": under 18 and not confirmed for this address.
export function guardianStateOf(entry) {
  if (!isMinorEntry(entry)) return "adult";
  const pf = entry?.personFields ?? {};
  const email = norm(pf.email);
  const confirmed = norm(pf.guardianRecipient?.address);
  return email && confirmed && email === confirmed ? "confirmed" : "needed";
}
export const needsGuardianCheck = (entry) => guardianStateOf(entry) === "needed";

/* ── the words ── */

// The callables' refusal codes (HttpsError message) in Ivin's words.
export function reviewErrorText(e) {
  const code = String(e?.message || "");
  if (code === "run_changed") return "The model changed since this page loaded. Look at the new one, then decide.";
  if (code === "photos_changed") return "The photographs changed since this page loaded. Look again, then decide.";
  if (code === "photos_not_ready") return "The photographs are not checked in the cloud yet. Approve once they are.";
  if (code === "nothing_to_review") return "There is no model to review yet.";
  if (code === "deleted") return "This capture was deleted.";
  if (code === "discarded") return "This capture was discarded. It is never sent.";
  if (code === "download_expired") return "The download period is over for everyone in it, so there is nothing to send.";
  // A delivery email about it, or a download code email to a page this
  // decision would leave with nothing on it.
  if (code === "send_in_progress") return "An email to a guest in this capture (about it, or a download code) is still on its way. Try again in a few minutes.";
  if (code === "too_many_runs") return "This capture has had too many models. Ask for help.";
  if (code === "too_many_captures") return "Someone in it already has 5 captures. Handle it by hand.";
  if (code === "too_many_members") return "More than 5 people in this capture. Handle it by hand.";
  if (code === "capture_not_complete") return "This capture is not complete.";
  if (code === "address_changed") return "The address changed while this was open. Look again.";
  if (code === "not_minor") return "This registration is not marked under 18.";
  if (code === "no_email") return "No email on file. Fix it with Fix address on the Never emailed tab first.";
  if (code === "not_found") return "Not found for this job.";
  if (/Requires role/.test(code)) return "Only an admin can do this.";
  return "Could not save. Try again.";
}

export const STAGE_LABEL = {
  review: "To review",
  held: "Held",
  reprocess: "Process again",
  approved: "Approved",
  photos_only: "Photos only",
  not_processed: "Not processed",
  left_out: "Left out",
  deleted: "Deleted",
};
// The status chip's kind (the design system's six meanings).
export const STAGE_CHIP = {
  review: "working",
  held: "paused",
  reprocess: "paused",
  approved: "live",
  photos_only: "live",
  not_processed: "stopped",
  left_out: "stopped",
  deleted: "stopped",
};

/* ── client versions (SafeShare 4.5) ──
 * A client job's versions (models, previews and the rest, never raw or
 * project) wait for Ivin's approval before a client can reach them; the
 * server's list (listVersionReview, functions/src/zelma/services/versionReview.ts)
 * says each one's stage and fingerprint, and every decision names that
 * fingerprint, so a version that changed meanwhile is refused, never taken. */

export const VERSION_FILTERS = [
  ["review", "To review"],
  ["held", "Held"],
  ["reprocess", "Process again"],
  ["removed", "Removed"],
  ["approved", "Approved"],
];
export const VERSION_STAGE_CHIP = { review: "working", held: "paused", reprocess: "paused", removed: "stopped", approved: "live" };
export const VERSION_STAGE_LABEL = Object.fromEntries(VERSION_FILTERS);

const KIND_WORD = {
  preview_splat: "Splat preview", preview_ar: "AR preview", preview_obj: "OBJ preview", contact_sheet: "Contact sheet",
  model: "Model", pointcloud: "Point cloud", hdr: "HDR", photoset: "Photo set",
};
/** "Model 40m", "Splat preview": the kind in words, and the label unless it is the default. */
export const versionTitle = (row) => {
  const word = KIND_WORD[row?.kind] ?? String(row?.kind ?? "");
  return row?.label && row.label !== "default" ? `${word} ${row.label}` : word;
};

/** What a version row may do now. Approve needs no deletion asked; an approved one is only taken back. */
export function versionActionsFor(row) {
  const s = row?.stage;
  const waiting = s === "review" || s === "held" || s === "reprocess" || s === "removed";
  return {
    approve: waiting && !row.deletionPending,
    hold: (s === "review" || s === "reprocess") && !row.deletionPending,
    reprocess: (s === "review" || s === "held") && !row.deletionPending,
    remove: s !== "removed",
    // Look opens any version's files (models too), pending or approved; a deletion asked: nothing is opened.
    look: Boolean(row) && !row.deletionPending,
  };
}

/** "Approve all that passed": waiting to review, nothing to look at first, and every reported check passed (at least one). */
export const approvableVersions = (rows) => (rows ?? []).filter((r) =>
  r.stage === "review" && !r.lookFirst && !r.deletionPending && passedChecks(r) && versionActionsFor(r).approve);

/** The decision callable's input: about exactly the version the list showed (its fingerprint). */
export const versionDecisionInput = (jobId, row, decision, note = "") => ({
  jobId, sn: row.sn, versionId: row.versionId, decision, seen: row.fingerprint,
  ...(note ? { note } : {}),
});

export function versionErrorText(e) {
  const code = String(e?.details?.reason || e?.message || "");
  if (code === "version_changed") return "This version changed since the list loaded. Look at it again, then decide.";
  if (code === "not_pending") return "It is already approved. Use Remove to take the approval back.";
  if (code === "not_reviewable") return "Raw and project files are not reviewed here.";
  if (code === "not_reported") return "This version is not finished yet.";
  if (code === "revision_chain_too_long") return "This model has too many older versions to place it. Ask for help.";
  if (/deletion/.test(code)) return "A deletion was asked for it, so it cannot be approved.";
  if (code === "asset_not_live" || code === "asset_missing") return "This capture is no longer live.";
  return reviewErrorText(e);
}
